import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, effect, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';


import { GoogleMapsLoaderService } from '@core/services/infra/google-maps-loader.service';
import { ThemeScopeService } from '@core/theme/theme-scope.service';
import { AuthService } from '@core/services/auth/auth.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { UnifiedLoadingService } from '@core/services/infra/unified-loading.service';
import { AuthenticatedLoadingCoordinatorService } from '@core/services/infra/authenticated-loading-coordinator.service';
import { AuthenticatedLoadingScreenComponent } from './shared/components/authenticated-loading-screen/authenticated-loading-screen.component';
import { EmailVerificationBannerComponent } from './shared/components/email-verification-banner/email-verification-banner.component';
import { BillingGracePeriodBannerComponent } from './shared/components/billing-grace-period-banner/billing-grace-period-banner.component';
import { ImpersonationBannerComponent } from './admin/components/impersonation-banner.component';
import { GlobalBannerOffsetDirective } from './shared/directives/global-banner-offset.directive';
import { CookieConsentBannerComponent } from './shared/components/cookie-consent-banner/cookie-consent-banner.component';
import { InvitationNotificationService } from '@core/services/auth/invitation-notification.service';
import { AnalyticsService } from '@core/services/infra/analytics.service';
import { SwUpdateService } from '@core/services/infra/sw-update.service';
import { setSentryUser } from '@core/observability/sentry';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { TranslationService, isLanguageSupported, SupportedLanguage } from './core/i18n';

@Component({
    selector: 'vex-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterOutlet, AuthenticatedLoadingScreenComponent, EmailVerificationBannerComponent, BillingGracePeriodBannerComponent, ImpersonationBannerComponent, GlobalBannerOffsetDirective, CookieConsentBannerComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private invitationService = inject(InvitationNotificationService);
  private translationService = inject(TranslationService);
  private authStore = inject(AuthStore);
  private workspaceStore = inject(WorkspaceStore);
  private analytics = inject(AnalyticsService);
  private swUpdate = inject(SwUpdateService);
  private mapsLoader = inject(GoogleMapsLoaderService);
  private themeScope = inject(ThemeScopeService);
  private isAuthenticated$ = toObservable(this.authStore.isAuthenticated);

  constructor(
    private authService: AuthService,
    private unifiedLoadingService: UnifiedLoadingService,
    private authenticatedLoadingCoordinator: AuthenticatedLoadingCoordinatorService
  ) {
    // Sync the user's language preference to the translation service whenever
    // the authenticated user changes. Runs in the injection context so
    // registration is automatic; no manual teardown needed.
    //
    // IMPORTANT: use `applyLanguage`, never `setLanguage`. The value here
    // already came from Firestore, so re-persisting it would round-trip
    // back through `onSnapshot` and fire this effect again. When two
    // rapid clicks have writes in flight, the lagging snapshot flips the
    // UI back to the previous language and the system oscillates until
    // the user gives up.
    effect(() => {
      const user = this.authStore.user();
      if (!user || !user.userUID) return;

      const userLanguage = user.preferences?.language;
      if (
        userLanguage &&
        isLanguageSupported(userLanguage) &&
        userLanguage !== this.translationService.currentLanguage
      ) {
        void this.translationService.applyLanguage(userLanguage as SupportedLanguage);
      }
    });

    // Tag Sentry events with WHO and WHERE, pseudonymously: the Firebase uid
    // and the active workspace, never a name or an e-mail. Separate from the
    // effect above on purpose — that one bails out when there is no user, and
    // we specifically need logout to CLEAR the identity. No-op unless the build
    // has a DSN. See core/observability/sentry.ts.
    effect(() => {
      const uid = this.authStore.user()?.userUID;
      const workspaceId = this.workspaceStore.currentWorkspaceId();
      setSentryUser(uid, workspaceId);
      // Same identity, the analytics side. Both are no-ops until their tool is
      // configured, and PostHog additionally until the visitor has accepted.
      this.analytics.identify(uid);
      this.analytics.setWorkspace(workspaceId);
    });
  }

  ngOnInit(): void {
    // Mirror the active route's `data.theme` onto <html>. Must start before the
    // first navigation settles so the tenant portal never paints one frame in
    // the manager palette.
    this.themeScope.start();

    // Boot Google Analytics (Consent Mode v2). No-op unless analytics is
    // enabled for this build; gtag stays consent-gated until the visitor accepts.
    this.analytics.init();

    // Watch for new app versions delivered by the service worker (prod only).
    // No-op when the SW is disabled (dev/Karma).
    this.swUpdate.init();

    // Warm up Google Maps (key fetch + bootstrap script + `places` library) in
    // the background so address autocomplete is already wired up by the time any
    // form mounts one — no more typing into a field that isn't ready yet.
    //
    // Gated on authentication: the Maps key comes from `getPublicConfig`, which
    // rejects unauthenticated callers with 401 (keys are vended only post-login,
    // by design). Preloading unconditionally at boot fired that 401 + a
    // "Failed to load runtime config" error in the console on every cold start.
    // A form that mounts an autocomplete still retries via `load()` regardless.
    this.isAuthenticated$
      .pipe(filter(Boolean), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.mapsLoader.preload());

    // Kick off unified loading; don't chain other work behind it.
    this.unifiedLoadingService.initializeApp().pipe(take(1)).subscribe({
      error: (error) => {
        console.error('❌ AppComponent: Failed to initialize application:', error);
      }
    });

    // Invitation check runs in parallel — it only needs auth, not full app load.
    this.checkForPendingInvitationsAfterInit();
  }

  /**
   * Check for pending invitations as soon as the user is authenticated,
   * independent of the main unified-loading pipeline.
   */
  private checkForPendingInvitationsAfterInit(): void {
    this.isAuthenticated$.pipe(
      filter(isAuth => isAuth === true),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      console.log('📬 AppComponent: Checking for pending invitations...');

      // Peek only — consuming here would wipe the ID before AuthShell can
      // open the accept dialog (Refs #584). AuthShell is the one-shot consumer.
      if (this.authService.hasPendingInvitation()) {
        console.log('📬 AppComponent: Found stored invitation ID, will be handled by AuthShell');
        return;
      }

      this.invitationService.checkForPendingInvitations().then(invitations => {
        if (invitations.length > 0) {
          console.log(`📬 AppComponent: Found ${invitations.length} pending invitation(s) - badge will update`);
        }
      }).catch(error => {
        console.error('❌ AppComponent: Error checking invitations:', error);
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
